import React, { useMemo } from 'react';
import { CustomAccordion } from '@planne-software/uni/Accordion';
import { IconCalendar, IconClock } from '@planne-software/uni/icons';

import { formatDate } from '@src/common';
import { useTranslate } from '@src/core/hooks';
import {
  countFilledFormInputGroups,
  firstPendingFormInputGroupKey,
  groupTariffUnits,
  isFormInputGroupFilled,
  formInputGroupKey,
  type FormInputValues,
  type SaleItemFormInputGroup,
} from '@src/domains/orders';
import type { SaleItemSummary } from '@src/features/orders/form-inputs/core/resolveSaleItemSummary';
import { resolveSaleGroupTitle } from '@src/features/orders/form-inputs/core/resolveSaleGroupTitle';
import { AdditionalInfoChip } from './AdditionalInfoChip';

import {
  SaleAccordionMeta,
  SaleAccordionMetaRow,
  SaleAccordionMetaText,
  SaleAccordionRoot,
  SaleAccordionTitle,
  SaleAccordionTitleRow,
  SellerAvatar,
  TariffList,
} from '../styles';

import { TariffRow } from './TariffRow';

type SaleAccordionProps = {
  group: SaleItemFormInputGroup;
  summary: SaleItemSummary | null;
  values: FormInputValues;
  groupsWithErrors: Set<string>;
  expanded: boolean;
  onToggle: (saleItemId: string) => void;
  onFillTariff: (saleItemId: string, tariffKey: string) => void;
  onEditTariff: (saleItemId: string, tariffId: string) => void;
};

export const SaleAccordion = (props: SaleAccordionProps) => {
  const { group, summary, values, groupsWithErrors, expanded, onToggle, onFillTariff, onEditTariff } = props;
  const { i18n } = useTranslate();

  const tariffs = useMemo(() => groupTariffUnits(group.formInputGroups), [group.formInputGroups]);

  const { total, filled } = useMemo(
    () => ({
      total: group.formInputGroups.length,
      filled: countFilledFormInputGroups(group.formInputGroups, values),
    }),
    [group.formInputGroups, values],
  );

  const complete = total > 0 && filled === total;

  return (
    <SaleAccordionRoot data-testid={`sale-accordion-${group.saleItemId}`}>
      <CustomAccordion
        expanded={expanded}
        onChange={() => onToggle(group.saleItemId)}
        // The chip goes in the title, not in the headerSlot: that wrapper is a column,
        // so from there it would land under the product name.
        title={
          <SaleAccordionTitleRow>
            <SaleAccordionTitle data-testid='sale-accordion-title'>{resolveSaleGroupTitle(summary)}</SaleAccordionTitle>
            <AdditionalInfoChip
              tone={complete ? 'success' : 'primary'}
              label={i18n('checkout_additional_info_progress', { filled: String(filled), total: String(total) })}
            />
          </SaleAccordionTitleRow>
        }
        // Seller and schedule live in the header because the design keeps them visible
        // while the accordion is collapsed.
        headerSlot={
          summary ? (
            <SaleAccordionMeta>
              <SaleAccordionMetaRow data-tight='true'>
                {summary.sellerLogoUrl ? <SellerAvatar src={summary.sellerLogoUrl} alt={summary.sellerName} /> : null}
                <SaleAccordionMetaText>{summary.sellerName}</SaleAccordionMetaText>
              </SaleAccordionMetaRow>

              {summary.scheduleDate || summary.scheduleTime ? (
                <SaleAccordionMetaRow>
                  {summary.scheduleDate ? (
                    <SaleAccordionMetaText>
                      <IconCalendar size={20} aria-hidden />
                      {formatDate(summary.scheduleDate)}
                    </SaleAccordionMetaText>
                  ) : null}
                  {summary.scheduleTime ? (
                    <SaleAccordionMetaText>
                      <IconClock size={20} aria-hidden />
                      {summary.scheduleTime}
                    </SaleAccordionMetaText>
                  ) : null}
                </SaleAccordionMetaRow>
              ) : null}
            </SaleAccordionMeta>
          ) : null
        }
        content={
          <TariffList>
            {tariffs.map((tariff) => (
              <TariffRow
                key={tariff.tariffId}
                tariff={tariff}
                filledUnits={tariff.units.filter((inputs) => isFormInputGroupFilled(inputs, values)).length}
                hasError={tariff.units.some((inputs) => groupsWithErrors.has(formInputGroupKey(inputs)))}
                onFill={() => onFillTariff(group.saleItemId, firstPendingFormInputGroupKey(tariff.units, values))}
                onEdit={() => onEditTariff(group.saleItemId, tariff.tariffId)}
              />
            ))}
          </TariffList>
        }
      />
    </SaleAccordionRoot>
  );
};

export default SaleAccordion;
